<template>
	<div class="verification-codes-manager">
		<ModalHeader
			:title="getString('additional_verification_codes')"
			:button-text="getString(newCoupon ? 'confirm' : 'save')"
			@click-button="saveVerificationCodes"
		/>
		<div class="verification-codes-container">
			<AppTable
				ref="verification-codes-table"
				:title="''"
				:items="currentShownCodes"
				:table-headings="verificationCodesHeadings"
				:total-items="filteredVerificationCodes.length"
				:allow-download="false"
				@changed-page="v => currentPageCodes = v"
				@changed-page-size="v => pageSizeCodes = v"
				@changed-sort="handleSort"
			>
				<template #custom-header>
					<div class="verification-header">
						<AppButton :text="getString('upload_csv')" outline small @click="importCodesFromFile" />
						<AppTextInput :placeholder="getString('search')" icon-name="icon-magnify"
									  :value="verificationSearchKey" @changed-text="updateVerificationSearchKey" />
					</div>
				</template>
				<template #code="{item}">
					<div class="edit-code" v-if="item.codeEdit">
						<input :ref="'edit-code-'+item.code" type="text" :value="item.code" @focusout="endEditCode($event, item)" @keyup.enter="endEditCode($event, item)" @input="onEditingCode($event, item)" />
					</div>
					<div class="table-edit-display" v-else @click="editCode(item)">
						<div class="standard-table-p">{{ item.code }}</div>
					</div>
				</template>
				<template #actions="{item}">
					<div class="row centered">
						<AppTableButtonGroup :buttons="codeButtons" @delete-code="removeVerificationCode(item.code)"/>
					</div>
				</template>
			</AppTable>
			<div class="add-code-container">
				<div class="row align-center gap-10">
					<AppTextInput ref="new-verification-code" :placeholder="getString('add_verification_code')"
						  class="code-input"
						  @keyup.enter="addVerificationCode"
						  @changing-text="onVerificationCodeChanging"
					/>
					<AppButtonIcon prevent-change icon-name="icon-plus-white" @click="addVerificationCode" />
				</div>
			</div>

		</div>
		<input ref="file-input" type="file" accept=".csv,.txt" @change="handleFileUpload" style="display: none;" />

		<AppPopup
			ref="import-confirm-popup"
			:title="errorPopupTitle"
			:buttons="importPopupButtons"
		>
			<div class="import-summary">
				<div v-if="importSummary.newCodes > 0" class="row flex-start mb-8">
					<img class="check-image" src="https://img.qromo.io/img/web-site/new/icon-rounded-ok.svg" alt="check">
					<p>
						<strong v-if="importSummary.newCodes > 1">{{ getString('codes_to_import', [(importSummary.newCodes - importSummary.excessCodes), importSummary.codesPreview]) }}</strong>
						<strong v-else>{{ getString('code_to_import', [importSummary.codesPreview]) }}</strong>
						<br><small>{{importSummary.codesPreview}}</small>
					</p>
				</div>
				<div v-if="importSummary.excessCodes > 0" class="row flex-start mb-8">
					<img class="check-image" src="https://img.qromo.io/img/web-site/new/icon-warning-yellow.svg" alt="warning">
					<p>
						<strong>{{getString('excess_codes', [importSummary.excessCodes])}}</strong>
						<br><small>{{importSummary.excessCodesPreview}}</small>
					</p>
				</div>
				<div v-if="importSummary.duplicates > 0" class="row flex-start mb-8">
					<img class="check-image" src="https://img.qromo.io/img/web-site/new/icon-decline.svg" alt="cross">
					<p>
						<strong>{{ getString('duplicate_codes_skipped', [importSummary.duplicates]) }}</strong>
						<br><small>{{importSummary.duplicatesPreview}}</small>
					</p>
				</div>
				<div v-if="importSummary.modified > 0" class="row flex-start align-start mb-8">
					<img class="check-image" src="https://img.qromo.io/img/web-site/new/icon-decline.svg" alt="cross">
					<p>
						<strong>{{ getString('invalid_characters_codes', [importSummary.modified]) }}</strong>
						<br><small>{{importSummary.modifiedPreview}}</small>
						<br><small class="opacity-60">{{getString('codes_modified_explain')}}</small>
					</p>
				</div>
			</div>
		</AppPopup>
	</div>
</template>

<script>
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppTable from "@/shared/components/AppTable.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import AppTableButtonGroup from "@/shared/components/AppTableButtonGroup.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import {sanitizeCouponVerificationCode} from "@/shared/helpers/strings";

export default {
	name: "VerificationCodesManager",
	components: {
		AppTableButtonGroup,
		ModalHeader,
		AppTable,
		AppButton,
		AppButtonIcon,
		AppTextInput,
		AppPopup
	},
	mixins: [WindowWidth],
	emits: ['save-codes'],
	props: {
		initialCodes: {
			type: Array,
			default: () => []
		},
		newCoupon: {
			type: Boolean,
			default: false
		}
	},
	setup() {
		const languageStore = useLanguageStore();
		const notificationStore = useNotificationStore();
		return {
			getString: languageStore.getString,
			showNotification: notificationStore.showNotification,
		}
	},
	data() {
		return {
			maxCouponVerificationCodes: 5000,
			verificationCodes: [],
			verificationSearchKey: null,
			pageSizeCodes: 10,
			currentPageCodes: 1,
			sortField: null,
			sortDirection: 'asc',
			sortAsc: null,
			importSummary: {
				tooLarge: false,
				excessCodes: 0,
				newCodes: 0,
				duplicates: 0,
				modified: 0,
				excessCodesPreview: '',
				codesPreview: '',
				duplicatesPreview: '',
				modifiedPreview: ''
			},
			pendingImportCodes: []
		}
	},
	computed: {
		errorPopupTitle() {
			if(this.importSummary?.tooLarge) {
				return this.getString('file_too_large_coupon');
			}
			return this.importSummary?.newCodes === 0 ? this.getString('no_code_found') : this.getString('confirm_import');
		},
		codeButtons() {
			return [
				{
					iconName: "icon-trash-primary",
					emitName: "delete-code",
				},
			];
		},
		verificationCodesHeadings() {
			return [
				{
					text: this.getString('code'),
					fieldName: 'code',
					slotName: 'code',
					sortable: true,
					cols: this.windowWidth >= 768 ? 3 : 2,
					noPadding: true,
					active: this.sortField === 'code',
					sortAsc: this.sortField === 'code' ? this.sortAsc : true
				},
				{
					text: this.getString('usage'),
					fieldName: 'usages',
					slotName: 'usages',
					sortable: true,
					cols: 1,
					formatter: us => Number(us || 0),
					active: this.sortField === 'usages',
					sortAsc: this.sortField === 'usages' ? this.sortAsc : true
				},
				{
					text: this.getString('actions'),
					fieldName: 'actions',
					slotName: 'actions',
					sortable: false,
					cols: 1,
				}
			];
		},
		currentCoupon() {
			return null;
		},
		currentShownCodes() {
			return this.sortedAndFilteredCodes.slice((this.currentPageCodes - 1) * this.pageSizeCodes, this.currentPageCodes * this.pageSizeCodes);
		},
		filteredVerificationCodes() {
			let codes = this.verificationCodes;
			if (this.verificationSearchKey) {
				codes = codes.filter(code =>
					code.code.toLowerCase().includes(this.verificationSearchKey.toLowerCase())
				);
			}
			return codes;
		},
		sortedAndFilteredCodes() {
			let codes = [...this.filteredVerificationCodes];

			if (this.sortField) {
				codes.sort((a, b) => {
					let aValue = a[this.sortField];
					let bValue = b[this.sortField];

					// Handle usages field (numeric comparison)
					if (this.sortField === 'usages') {
						aValue = Number(aValue || 0);
						bValue = Number(bValue || 0);
					}
					// Handle code field (string comparison)
					else if (this.sortField === 'code') {
						aValue = (aValue || '').toLowerCase();
						bValue = (bValue || '').toLowerCase();
					}

					if (aValue < bValue) return this.sortAsc ? -1 : 1;
					if (aValue > bValue) return this.sortAsc ? 1 : -1;
					return 0;
				});
			}

			return codes;
		},
		importPopupButtons() {
			if(this.importSummary?.newCodes === 0 || this.importSummary?.tooLarge) {
				return [
					{
						text: this.getString('ok'),
						callback: this.cancelImport,
						negative: true
					}
				];
			}

			return [
				{
					text: this.getString('cancel'),
					callback: this.cancelImport,
					negative: true
				},
				{
					text: this.getString('import'),
					callback: this.confirmImport,
					disabled: this.importSummary.newCodes === 0
				}
			];
		}
	},
	methods: {
		initializeVerificationCodes() {
			const propCodes = this.initialCodes;
			if (propCodes && Array.isArray(propCodes)) {
				this.verificationCodes = propCodes.map((codeData, index) => {
					if (typeof codeData === 'string') {
						return {
							code: codeData,
							codeEdit: false,
							usages: 0
						};
					} else if (typeof codeData === 'object' && codeData !== null) {
						return {
							code: codeData.code || codeData.verification_code || '',
							codeEdit: false,
							usages: codeData.usages || 0
						};
					}
					return null;
				}).filter(item => item !== null && item.code);
			} else {
				this.verificationCodes = [];
			}
			this.currentPageCodes = 1;
			this.verificationSearchKey = null;
		},
		addVerificationCode() {
			const codeInput = this.$refs['new-verification-code'];
			const codeResult = this.sanitizeCode((codeInput?.currentValue || '').trim());
			if (this.$refs['new-verification-code']) {
				this.$refs['new-verification-code'].currentValue = codeResult.sanitized;
			}
			if (!codeResult.sanitized || !codeInput || !codeInput?.currentValue || codeInput.currentValue.trim() === '') {
				this.showNotification('insert-valid-code');
				return;
			}
			if (this.verificationCodes.some(item => item.code === codeResult.sanitized)) {
				this.showNotification('coupon-additional-code-exists');
				return;
			}
			let excessCodes = this.verificationCodes.length - this.maxCouponVerificationCodes;
			if(excessCodes >= 0) {
				this.showNotification('max-5000-coupon-codes', [1])
			} else {
				this.verificationCodes.push({
					id: null,
					code: codeResult.sanitized,
					usages: 0
				});
				codeInput.currentValue = '';
			}
		},
		removeVerificationCode(code) {
			this.verificationCodes = this.verificationCodes.filter(item => item.code !== code);

			// Check if current page is now empty and we're not on the first page
			const totalItems = this.sortedAndFilteredCodes.length;
			const maxPage = Math.ceil(totalItems / this.pageSizeCodes) || 1;

			if (this.currentPageCodes > maxPage) {
				// Navigate to the last valid page
				this.currentPageCodes = maxPage;
				this.changePage(maxPage);
			}
		},
		saveVerificationCodes() {
			// Save the codes back to the coupon
			this.$emit('save-codes', this.verificationCodes.map(item => item.code));
		},
		importCodesFromFile() {
			this.$refs['file-input'].click();
		},
		handleFileUpload(event) {
			const file = event.target.files[0];
			if (!file) {
				return;
			}
			let oneKb = 1024;
			if (file.size > 200 * oneKb) {
				this.importSummary.tooLarge = true;
				this.$refs['import-confirm-popup'].toggleStatus(true);
				return;
			}
			const reader = new FileReader();
			reader.onload = (e) => {
				try {
					const content = e.target.result;
					const codes = this.parseFileContent(content);
					this.prepareImportSummary(codes);
					// Reset the file input
					event.target.value = '';
				} catch (error) {
					this.showNotification('error-parsing-file');
				}
			};
			reader.readAsText(file);
		},
		parseFileContent(content) {
			// Split by newlines first to get rows
			const lines = content.split(/\r?\n/);
			const codes = [];

			lines.forEach(line => {
				if (line.trim()) {
					// For CSV, take the first column (split by comma)
					const firstColumn = line.split(',')[0].trim();
					if (firstColumn) {
						codes.push(firstColumn);
					}
				}
			});

			return codes;
		},
		sanitizeCode(code) {
			const original = (code || '');

			// Replace spaces with dashes
			let sanitizedCode = sanitizeCouponVerificationCode(code);
			return {
				original: original,
				sanitized: sanitizedCode,
				modified: original.toUpperCase() !== sanitizedCode
			};
		},
		prepareImportSummary(rawCodes) {
			this.pendingImportCodes = [];
			let excessCodesCount = 0;
			let newCodesCount = 0;
			let duplicatesCount = 0;
			let modifiedCount = 0;
			let previewExcess = '';
			let previewCodes = '';
			let previewDuplicates = '';
			let previewModified = '';

			rawCodes.forEach(rawCode => {
				const codeResult = this.sanitizeCode(rawCode);

				if (!codeResult.sanitized) return;

				// Check for duplicates in existing codes
				const isDuplicate = this.pendingImportCodes.concat(this.verificationCodes).some(item => (item?.code || '').toUpperCase() === codeResult.sanitized);
				if (!isDuplicate) {
					// Check if this code is already in our pending list (remove duplicates within file)
					const alreadyPending = this.pendingImportCodes.some(item => item.code === codeResult.sanitized);

					if (!alreadyPending) {
						this.pendingImportCodes.push({
							original: codeResult.original,
							code: codeResult.sanitized,
							modified: codeResult.modified
						});
						newCodesCount++;

						if (newCodesCount < 10) {
							previewCodes += (previewCodes ? ', ' : '') + codeResult.sanitized;
						} else if (newCodesCount === 10) {
							previewCodes += ', ...';
						}

						if (codeResult.modified) {
							modifiedCount++;
							if (modifiedCount < 10) {
								previewModified += (previewModified ? ', ' : '') + (codeResult?.original || '').toUpperCase() + ' → ' + codeResult.sanitized;
							} else if (modifiedCount === 10) {
								previewModified += ', ...';
							}
						}

						if (this.pendingImportCodes.length + this.verificationCodes.length > this.maxCouponVerificationCodes) {
							excessCodesCount++;
							if (excessCodesCount < 10) {
								previewExcess += (previewExcess ? ', ' : '') + codeResult.sanitized;
							} else if (excessCodesCount === 10) {
								previewExcess += ', ...';
							}
						}
					}
				} else {
					duplicatesCount++;
					if (duplicatesCount < 10) {
						previewDuplicates += (previewDuplicates ? ', ' : '') + codeResult.sanitized;
					} else if (duplicatesCount === 10) {
						previewDuplicates += ', ...';
					}
				}
			});

			this.importSummary = {
				tooLarge: false,
				excessCodes: excessCodesCount > 0 ? excessCodesCount : 0,
				newCodes: newCodesCount,
				duplicates: duplicatesCount,
				modified: modifiedCount,
				codesPreview: previewCodes,
				excessCodesPreview: previewExcess,
				duplicatesPreview: previewDuplicates,
				modifiedPreview: previewModified
			};
			// If there are no new codes, modified codes or duplicates, show the popup to inform the user
			if (newCodesCount === 0 || modifiedCount > 0 || duplicatesCount > 0) {
				this.$refs['import-confirm-popup'].toggleStatus(true);
			} else {
				this.importPendingCodes();
			}
		},
		importPendingCodes() {
			this.pendingImportCodes.forEach(codeData => {
				if(this.verificationCodes.length < this.maxCouponVerificationCodes) {
					this.verificationCodes.push({
						id: null,
						code: codeData.code,
						usages: 0
					});
				}
			});
			if (this.importSummary.newCodes > 0 && this.importSummary.excessCodes <= 0) {
				//this.showNotification('success-saved-x-coupon-codes', [this.importSummary.newCodes]);
			} else if (this.importSummary.excessCodes > 0) {
				this.showNotification('max-5000-coupon-codes', [this.importSummary.excessCodes]);
			}

			// Clear pending data
			this.pendingImportCodes = [];
			this.importSummary = { tooLarge: false, excessCodes: 0, newCodes: 0, duplicates: 0, modified: 0, excessCodesPreview: '', codesPreview: '', duplicatesPreview: '', modifiedPreview: '' };
		},
		confirmImport() {
			this.importPendingCodes();
			this.$refs['import-confirm-popup'].toggleStatus(false);
		},
		cancelImport() {
			// Clear pending data
			this.pendingImportCodes = [];
			this.importSummary = { tooLarge: false, excessCodes: 0, newCodes: 0, duplicates: 0, modified: 0, excessCodesPreview: '', codesPreview: '', duplicatesPreview: '', modifiedPreview: '' };

			// Close popup
			this.$refs['import-confirm-popup'].toggleStatus(false);
			if (this.$refs['file-input']) {
				this.$refs['file-input'].value = '';
			}
		},
		changePage(activePage) {
			let tableRef = this.$refs['verification-codes-table'];
			if (tableRef && tableRef?.$refs?.['pagination-btns']) {
				tableRef.$refs['pagination-btns'].activePage = activePage;
			}
		},
		updateVerificationSearchKey(key) {
			this.verificationSearchKey = key;
			this.currentPageCodes = 1;
			this.changePage(1);
		},
		onVerificationCodeChanging(value) {
			// Replace spaces with dashes in real-time
			const sanitizedValue = value.replace(/\s+/g, '-');
			if (this.$refs['new-verification-code']) {
				this.$refs['new-verification-code'].currentValue = sanitizedValue;
			}
		},
		editCode(item) {
			item.codeEdit = true;
			this.$nextTick(() => {
				if(this.$refs.hasOwnProperty("edit-code-"+item.code)) {
					this.$refs["edit-code-"+item.code].focus();
				}
			});
		},
		endEditCode(event, item) {
			let inputEl = this.$refs["edit-code-"+item.code] || event.target;
			if(!inputEl) { return; }
			const newValue = (inputEl?.value || '').trim();
			const codeResult = this.sanitizeCode(newValue);

			if (!codeResult.sanitized) {
				this.showNotification('insert-valid-code');
				item.codeEdit = false;
				return;
			}

			// Check for duplicates (excluding the current item)
			if (this.verificationCodes.some(code => code.code !== item.code && code.code === codeResult.sanitized)) {
				this.showNotification('coupon-additional-code-exists');
				item.codeEdit = false;
				return;
			}

			if(codeResult.sanitized !== item.code) {
				item.code = codeResult.sanitized;
			}
			item.codeEdit = false;
		},
		onEditingCode(event, item) {
			const sanitizedValue = event.target.value.replace(/\s+/g, '-');
			if (event.target.value !== sanitizedValue) {
				event.target.value = sanitizedValue;
			}
		},
		handleSort(newSortField, newSortAsc) {
			this.sortField = newSortField;
			this.sortAsc = !!newSortAsc;
			this.currentPageCodes = 1;
			this.changePage(1);
		},
	},
	mounted() {
		this.initializeVerificationCodes();
	},
	watch: {
		initialCodes: {
			handler(newVal) {
				if (newVal) {
					this.initializeVerificationCodes();
				}
			},
			immediate: true
		}
	},
}
</script>

<style scoped lang="scss">
.verification-codes-manager {

	.verification-codes-container {
		margin-top: 20px;
	}

	.verification-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		width: 100%;
		gap: 10px;

		.input-wrapper {
			max-width: 300px;
		}
	}

	.standard-table-p {
		max-width: 100%;
		overflow-wrap: break-word;
		hyphens: auto;
		font-size: 14px;
		line-height: 18px;
		padding: 8px;
	}

	.add-code-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 10px;
		padding: 10px 12px;
		background-color: var(--color-bg-primary-2);
		border-radius: 13px;
		margin-top: 20px;

		.code-input {
			flex: 1;
		}
	}
}

@media (max-width: 768px) {
	.verification-codes-manager {
		.add-code-container {
			padding: 10px 8px;
		}
	}
}
@media (max-width: 600px) {
	.verification-header {
		flex-direction: column;
		align-items: stretch;

		.input-wrapper {
			max-width: 100%;
		}
	}
}

.table-edit-display {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	cursor: pointer;
	width: 100%;
	height: 100%;
	padding: 5px;
	background-color: var(--color-alpha-1);

	.standard-table-p {
		max-width: 100%;
		overflow-wrap: break-word;
		hyphens: auto;
		font-size: 12px;
		line-height: 16px;
		@include noSelect;
	}
}

.edit-code {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	padding: 5px;
	background-color: var(--color-bg-primary-2);

	input {
		width: 100%;
		height: 100%;
		border: none;
		color: var(--color-txt);
		font-size: 12px;
		background-color: var(--color-bg-primary-2);
		text-transform: uppercase;
	}
}

.import-summary {
	padding: 16px 0;
	width: 100%;

	p {
		font-size: 14px;
		line-height: 1.5;
		max-width: calc(100% - 50px);
	}
	img.check-image {
		width: 15px;
		height: 15px;
		margin-right: 10px;
		margin-top: 4px;
	}

	.warning-text {
		color: var(--color-warning);
	}

	.error-text {
		color: var(--color-danger);
	}
}
</style>